import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import API_BASE_URL from '../config/api';
import {
  Eye,
  EyeOff,
  Building,
  BarChart3,
  Clock,
  FileText,
  Calendar,
  Trash2,
  Loader2,
  AlertTriangle
} from 'lucide-react';
import { getJobApplications } from '../utils/jobExtractor';
import axios from 'axios';

interface Application {
  id?: string;
  title: string;
  company: string;
  location?: string;
  date: string;
}

interface ApplicationJournalProps {
  isOpen: boolean;
  onToggle: () => void;
  analysisHistory: any[];
  cvScoreHistory: any;
  loading: boolean;
  session: any;
  onRefreshHistory: () => void;
}

const ApplicationJournal: React.FC<ApplicationJournalProps> = ({ 
  isOpen, 
  onToggle, 
  analysisHistory, 
  cvScoreHistory, 
  loading: isLoading, 
  session, 
  onRefreshHistory 
}) => {
  const { t } = useTranslation();
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showClearHistoryModal, setShowClearHistoryModal] = useState(false);
  
  const applications: Application[] = getJobApplications();

  // Memoized score styling function for better performance
  const getScoreStyling = useCallback((score: number) => {
    if (score >= 80) {
      return "bg-green-100 text-green-800";
    } else if (score >= 60) {
      return "bg-yellow-100 text-yellow-800";
    } else {
      return "bg-orange-100 text-orange-800";
    }
  }, []);

  // Clear analysis history via API
  const clearAnalysisHistory = async () => {
    if (!session?.access_token) {
      console.warn('No access token available for clearing analysis history');
      return;
    }

    try {
      setIsClearing(true);
      setErrorMessage(null);
      
      const response = await axios.post(
        `${API_BASE_URL}/api/clearCvAnalysisHistory`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.code === 200 && response.data.status === 'success') {
        setShowSuccessMessage(true);
        setTimeout(() => {
          setShowSuccessMessage(false);
        }, 3000);
        // Refresh the history from parent
        onRefreshHistory();
      }
    } catch (error: any) {
      console.error('Error clearing analysis history:', error);
      setErrorMessage(
        error?.response?.data?.error || 
        error?.message || 
        'Failed to clear analysis history'
      );
    } finally {
      setIsClearing(false);
    }
  };

  const clearAllHistory = async () => {
    // Clear via API if authenticated, otherwise fallback to localStorage
    if (session?.access_token) {
      await clearAnalysisHistory();
    } else {
      // Fallback to localStorage for local mode
      const userId = JSON.parse(localStorage.getItem('cv_analyzer_auth') || '{}').id;
      if (userId) {
        localStorage.removeItem(`cv_analyzer_analyses_${userId}`);
      }
      
      // Show success message
      setShowSuccessMessage(true);
      setTimeout(() => {
        setShowSuccessMessage(false);
        window.location.reload();
      }, 1500);
    }
    setShowClearHistoryModal(false);
  };

  if (!isOpen) {
    return (
      <div 
        className="bg-white rounded-xl shadow-md border border-gray-200 mb-6 cursor-pointer hover:shadow-lg transition-all transform hover:scale-[1.01]"
        onClick={onToggle}
      >
        <div className="w-full p-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-purple-100 rounded-lg">
              <BarChart3 className="w-5 h-5 text-purple-600" />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-gray-900">{t('application_journal.analysis_history')}</h3>
              <p className="text-sm text-gray-600">
                {analysisHistory.length + applications.length} {t('application_journal.matches_performed')} • 
                {cvScoreHistory?.history_total_companies} {t('application_journal.companies_analyzed')}
              </p>
            </div>
          </div>
          {/* <Eye className="w-5 h-5 text-gray-400" /> */}
        </div>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-200 mb-6">
      {/* En-tête cliquable */}
      <div 
        className="p-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-indigo-50 cursor-pointer hover:from-purple-100 hover:to-indigo-100 transition-all rounded-t-xl"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-purple-500 rounded-lg">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{t('application_journal.analysis_history')}</h3>
              <p className="text-sm text-gray-600">
                {analysisHistory.length + applications.length} {t('application_journal.matches_performed')} • 
                {cvScoreHistory?.history_total_companies} {t('application_journal.companies_analyzed')}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {analysisHistory.length > 0 && <button
              onClick={(e) => {
                e.stopPropagation();
                setShowClearHistoryModal(true);
              }}
              disabled={isClearing}
              className="flex items-center px-3 py-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              title={t('application_journal.clear_history_button')}
            >
              {isClearing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              <span className="ml-1 hidden sm:inline">{isClearing ? t('application_journal.clearing') : t('application_journal.clear_history_button')}</span>
            </button>}
            
            {/* <div className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
              <EyeOff className="w-5 h-5" />
            </div> */}
          </div>
        </div>
      </div>

      {/* Contenu */}
      <div className="p-6">
        {/* Message de succès */}
        {showSuccessMessage && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center animate-slideIn">
            <div className="w-5 h-5 border-2 border-green-500 border-t-transparent rounded-full animate-spin mr-3"></div>
            <span className="text-green-800 font-medium">{t('application_journal.history_cleared_successfully')}</span>
          </div>
        )}

        {/* Error Message */}
        {errorMessage && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center">
            <span className="text-red-800 font-medium">{errorMessage}</span>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="mb-4 p-4 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-purple-600 mr-2" />
            <span className="text-gray-600">{t('application_journal.loading_history')}</span>
          </div>
        )}

        {/* No Data State */}
        {!isLoading && analysisHistory.length === 0 && applications.length === 0 && (
          <div className="mb-4 p-8 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('application_journal.no_applications.title')}</h3>
            <p className="text-gray-600">{t('application_journal.no_applications.description')}</p>
          </div>
        )}
        
        {/* Analysis History List */}
        {!isLoading && (analysisHistory.length > 0 || applications.length > 0) && (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {/* Real Analysis History from API */}
          {analysisHistory.map((analysis, index) => (
            <div
              key={`api-${analysis.userId}-${index}`}
              className="bg-white rounded-lg p-4 border border-gray-200 flex items-start justify-between"
            >
              <div className="flex items-start space-x-3 flex-1 min-w-0">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-4 h-4 text-blue-600" />
                  </div>
                </div>
                
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-gray-900 mb-1">
                    {analysis.jobTitle}
                  </h4>
                  
                  <div className="flex items-center space-x-4 text-sm text-gray-600 mb-2">
                    <div className="flex items-center">
                      <Building className="w-3 h-3 mr-1" />
                      <span>{analysis.jobCompanyName || t('application_journal.company_not_detected')}</span>
                    </div>
                    
                    <div className="flex items-center">
                      <Calendar className="w-3 h-3 mr-1" />
                      <span>{new Date(analysis.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>

                  <div className="flex items-center text-sm text-gray-500">
                    <Clock className="w-3 h-3 mr-1" />
                    <span className="truncate">
                      {analysis.jobDescription.length > 100 
                        ? `${analysis.jobDescription.substring(0, 100)}...` 
                        : analysis.jobDescription
                      }
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="flex-shrink-0 ml-3 text-right">
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getScoreStyling(analysis.globalScore)}`}>
                  {t('application_journal.analyzed')}
                </div>
                <div className="text-xs text-gray-500 mt-1 text-center font-bold">
                  {analysis.globalScore}%
                </div>
              </div>
            </div>
          ))}
          
          {/* Local Applications */}
          {applications.map((analysis, index) => (
            <div
              key={analysis.id || index}
              className="bg-white rounded-lg p-4 border border-gray-200 flex items-start justify-between"
            >
              <div className="flex items-start space-x-3 flex-1 min-w-0">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-4 h-4 text-purple-600" />
                  </div>
                </div>
                
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-gray-900 mb-1">
                    {analysis.title}
                  </h4>
                  
                  <div className="flex items-center space-x-4 text-sm text-gray-600 mb-2">
                    <div className="flex items-center">
                      <Building className="w-3 h-3 mr-1" />
                      <span>{analysis.company || t('application_journal.company_not_detected')}</span>
                    </div>
                    
                    <div className="flex items-center">
                      <Calendar className="w-3 h-3 mr-1" />
                      <span>{analysis.date}</span>
                    </div>
                  </div>

                  {analysis.location && (
                    <div className="flex items-center text-sm text-gray-500">
                      <Clock className="w-3 h-3 mr-1" />
                      <span>{analysis.location}</span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex-shrink-0 ml-3 text-right">
                <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                  {t('application_journal.analyzed')}
                </div>
              </div>
            </div>
          ))}
        </div>
        )}

        {/* Statistiques en bas */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
            <div className="bg-purple-50 rounded-lg p-3">
              <div className="text-lg font-bold text-purple-600">
                {cvScoreHistory ? cvScoreHistory.history_total_applications : analysisHistory.length + applications.length}
              </div>
              <div className="text-xs text-purple-800">{t('application_journal.statistics.applications')}</div>
            </div>
            
            <div className="bg-blue-50 rounded-lg p-3">
              <div className="text-lg font-bold text-blue-600">
                {cvScoreHistory ? cvScoreHistory.history_total_companies : new Set(
                  analysisHistory
                    .map(item => item.jobCompanyName)
                    .filter(name => name && name.trim() !== '')
                    .concat(
                      applications
                        .map(app => app.company)
                        .filter(name => name && name.trim() !== '')
                    )
                ).size}
              </div>
              <div className="text-xs text-blue-800">{t('application_journal.statistics.companies')}</div>
            </div>
            
            <div className="bg-green-50 rounded-lg p-3">
              <div className="text-lg font-bold text-green-600">
                {cvScoreHistory && cvScoreHistory.history_average_score !== "0" 
                  ? `${cvScoreHistory.history_average_score}%` 
                  : analysisHistory.length > 0 
                    ? `${Math.round(analysisHistory.reduce((sum, item) => sum + parseInt(item.globalScore), 0) / analysisHistory.length)}%`
                    : "0%"
                }
              </div>
              <div className="text-xs text-green-800">{t('application_journal.statistics.average_score')}</div>
            </div>
            
            <div className="bg-orange-50 rounded-lg p-3">
              <div className="text-lg font-bold text-orange-600">
                {cvScoreHistory && cvScoreHistory.history_top_match !== "0" 
                  ? `${cvScoreHistory.history_top_match}` 
                  : analysisHistory.length > 0 
                    ? Math.max(...analysisHistory.map(item => parseInt(item.globalScore)))
                    : "0"
                }
              </div>
              <div className="text-xs text-orange-800">{t('application_journal.statistics.top_match')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Clear History Confirmation Modal */}
      {showClearHistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md mx-4">
            <div className="p-6 text-center">
              {/* Warning Icon */}
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-8 h-8 text-red-600" />
                </div>
              </div>
              
              {/* Title */}
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {t('application_journal.clear_confirm_title')}
              </h3>
              
              {/* Description */}
              <p className="text-gray-600 mb-6">
                {t('application_journal.clear_confirm_description')}
              </p>
              
              {/* Warning Box */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6 text-left">
                <div className="flex items-start">
                  <AlertTriangle className="w-5 h-5 text-orange-600 mr-2 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-orange-800 mb-1">
                      {t('application_journal.clear_warning_title')}
                    </h4>
                    <p className="text-sm text-orange-700">
                      {t('application_journal.clear_warning_single', { count: analysisHistory.length})}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowClearHistoryModal(false)}
                  disabled={isClearing}
                  className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('application_journal.clear_cancel')}
                </button>
                <button
                  onClick={clearAllHistory}
                  disabled={isClearing}
                  className="flex-1 flex items-center justify-center px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isClearing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('application_journal.clearing')}
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      {t('application_journal.clear_confirm_button')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationJournal;